import { useState, useCallback, useRef } from "react";
import { PaginatedResponse } from "../types/interfaces/app/PaginatedResponse";

interface UsePaginatedAutocompleteOptions<T> {
  fetchFn: (search: string, offset: number, limit: number) => Promise<PaginatedResponse<T>>;
  limit?: number;
}

export function usePaginatedAutocomplete<T extends { id: number }>({
  fetchFn,
  limit = 50,
}: UsePaginatedAutocompleteOptions<T>) {
  const [options, setOptions] = useState<T[]>([]);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [search, setSearch] = useState("");
  const offsetRef = useRef(0);
  const searchRef = useRef("");
  const abortRef = useRef<AbortController | null>(null);
  const loadingRef = useRef(false);

  const load = useCallback(async (newSearch: string, reset: boolean) => {
      if (loadingRef.current) return;
      loadingRef.current = true;
      abortRef.current?.abort();
      abortRef.current = new AbortController();
      const offset = reset ? 0 : offsetRef.current;
      setLoading(true);
      try {
        const response = await fetchFn(newSearch, offset, limit);
        setOptions((prev) => reset ? response.data : [...prev, ...response.data]);
        offsetRef.current = offset + response.data.length;
        setHasMore(offsetRef.current < response.total);
      } catch (err: any) {
        if (err.name !== "AbortError" && err.name !== "CanceledError") throw err;
      } finally {
        setLoading(false);
        loadingRef.current = false;
      }
    },
    [fetchFn, limit],
  );

  const onInputChange = useCallback(
    (value: string) => {
      setSearch(value);
      searchRef.current = value;
      offsetRef.current = 0;
      load(value, true);
    },
    [load],
  );

  const loadMore = useCallback(() => {
    if (!loadingRef.current && hasMore) load(searchRef.current, false);
  }, [hasMore, load]);

  const reset = useCallback(() => {
    setOptions([]);
    offsetRef.current = 0;
    setHasMore(true);
  }, []);

  return { options, loading, hasMore, search, onInputChange, loadMore, reset };
}